import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { Modal, Spin, Progress, Table, Tooltip } from 'antd';
import Icon from '@ant-design/icons';
import { saveAs } from 'file-saver';
import classnames from 'classnames';
import { mediaHook } from 'customHooks';

import { createMultipleProducts } from 'services/products';
import { COUNTRY_CURRENCY_MODIFIED } from 'constants/helpers';
import {
  FILE_SHEET_UPLOAD_ERROR_TYPES,
  IMPORT_MULTIPLE_PRODUCTS_HELP_LINK,
  MULTIPLE_IMPORT_STEPS_KEYS,
  SHEET_MAX_NUM_OF_PRODUCTS
} from 'constants/products';
import { UPLOADED_FILE_TYPES } from 'constants/common';
import { COD_MIN_MAX } from 'constants/shipments';
import { LOCALE } from 'constants/intl-wrapper';
import {
  convertSheetToJSON,
  findLowerOriginalPriceIndices
} from 'utils/products';
import { sleep, stringToBoolean } from 'utils/helpers';
import { sendSegment } from 'utils/segment';

import UploadAttachment from 'components/SupportTickets/components/UploadAttachment/UploadAttachment';
import BRButton from 'components/BRButton/BRButton';
import { notify } from 'components/Notify/Notify';

import ProductsSampleXLSX from 'assets/Products Sample XLSX - Add Products Here.xlsx';
import { ReactComponent as InfoCircleIcon } from 'assets/bosta-icons/Info-Circle.svg';
import { ReactComponent as ExcelIcon } from 'assets/bosta-icons/excel.svg';
import { ReactComponent as AlertTriangleIcon } from 'assets/bosta-icons/alert-triangle.svg';
import { ReactComponent as ExternalLinkIcon } from 'assets/bosta-icons/Teal-External-link.svg';
import BulkIcon from 'assets/icons/bulk-create.svg';
import YoutubeReference from 'assets/icons/youtube-bulk-upload.png';
import { ReactComponent as CloseIcon } from 'assets/bosta-icons/Close.svg';

import './ImportMultipleProductsModal.less';

const { UPLOAD_FILE, PREVIEW_FILE, VALIDATING_AND_UPLOADING, PREVIEW_ERRORS } =
  MULTIPLE_IMPORT_STEPS_KEYS;

const ErrorAlert = ({ msg }) => {
  return (
    <div className="br-preview-uploaded-file__error">
      <div>
        <Icon component={AlertTriangleIcon} />
      </div>
      <div>{msg}</div>
    </div>
  );
};

const UploadFile = ({
  intl,
  isSmallMobileScreen,
  isUploading,
  beforeUpload,
  renderSampleTemplate,
  renderYoutubeVideo
}) => {
  return (
    <div className="br-upload-file">
      <div className="br-import-multiple-products-modal__info">
        <div>
          <Icon component={InfoCircleIcon} />
        </div>
        <div>
          {intl.formatMessage(
            {
              id: 'products.import_multiple_products.info'
            },
            {
              span: renderSampleTemplate
            }
          )}
        </div>
      </div>
      <span className="body-medium">
        {intl.formatMessage({
          id: 'products.import_multiple_products.upload_sheet'
        })}
      </span>
      {isUploading ? (
        <div className="br-upload-file__spin-container">
          <Spin />
        </div>
      ) : (
        <UploadAttachment
          customTitle={
            isSmallMobileScreen ? (
              <></>
            ) : (
              intl.formatMessage({
                id: 'products.import_multiple_products.upload.title'
              })
            )
          }
          accept=".csv, .xlsx"
          customSubtitle={intl.formatMessage(
            {
              id: 'products.import_multiple_products.upload.subtitle'
            },
            {
              maxNumOfProducts: SHEET_MAX_NUM_OF_PRODUCTS
            }
          )}
          beforeUpload={beforeUpload}
          multiple={false}
          showUploadList={false}
          uploadIcon={BulkIcon}
        />
      )}
      {renderYoutubeVideo()}
    </div>
  );
};

const PreviewFile = ({
  intl,
  sheetData,
  errorsCount,
  renderSampleTemplate,
  renderYoutubeVideo,
  handleReplace
}) => {
  const { name, size, rowsData, fileError } = sheetData || {};

  return (
    <div className="br-preview-uploaded-file">
      {fileError && <ErrorAlert msg={fileError} />}
      {!fileError && renderSampleTemplate && (
        <div className="br-import-multiple-products-modal__info">
          <div>
            <Icon component={InfoCircleIcon} />
          </div>
          <div>
            {intl.formatMessage(
              {
                id: 'products.import_multiple_products.info'
              },
              {
                span: renderSampleTemplate
              }
            )}
          </div>
        </div>
      )}
      <div className="br-preview-uploaded-file__content">
        <div className="br-preview-uploaded-file__content__data">
          <div>
            <div className="br-preview-uploaded-file__icon">
              <Icon component={ExcelIcon} />
            </div>
            <div className="br-preview-uploaded-file__title-subtitle">
              <div className="br-preview-uploaded-file__title">
                {intl.formatMessage(
                  {
                    id: 'products.import_multiple_products.file_name'
                  },
                  {
                    fileName: name || 'N/A'
                  }
                )}
              </div>
              <div className="br-preview-uploaded-file__subtitle">
                <span>
                  {intl.formatMessage({
                    id: 'products.import_multiple_products.total_products'
                  })}
                  <span
                    className={classnames('br-preview-uploaded-file__data', {
                      error: !!fileError
                    })}
                  >
                    {rowsData?.length}
                  </span>
                </span>
                {!!errorsCount && (
                  <span>
                    {intl.formatMessage(
                      {
                        id: 'products.import_multiple_products.file_size'
                      },
                      {
                        span: (children) => (
                          <span className="br-preview-uploaded-file__data">
                            {children}
                          </span>
                        ),
                        fileSize: (size / 1024 / 1024).toFixed(2)
                      }
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="br-preview-uploaded-file__content__replace-btn">
            <UploadAttachment
              accept=".csv, .xlsx"
              beforeUpload={handleReplace}
              multiple={false}
              showUploadList={false}
            >
              <BRButton
                label={intl.formatMessage({
                  id: 'products.import_multiple_products.replace_file'
                })}
              />
            </UploadAttachment>
          </div>
        </div>
        {!!errorsCount && (
          <ErrorAlert
            msg={intl.formatMessage(
              {
                id: 'products.import_multiple_products.uploading.error_msgs.validations'
              },
              {
                count: errorsCount
              }
            )}
          />
        )}
      </div>
      {renderYoutubeVideo && renderYoutubeVideo()}
    </div>
  );
};

const ValidatingAndUploading = ({
  intl,
  sheetData,
  errorsObj,
  setCurrentStepKey,
  setErrorsObj,
  setIsUploadBtnDisabled,
  addMultipleProducts
}) => {
  const [percent, setPercent] = useState(0);

  let timer;

  const finishValidatingAndUploading = async () => {
    clearInterval(timer);
    setPercent(100);
    await sleep(1000);
    if (errorsObj.count) {
      setCurrentStepKey(PREVIEW_ERRORS);
    }
  };

  const startValidatingAndUploading = () => {
    let randomNumber = 1;
    timer = setInterval(() => {
      randomNumber = Math.floor(Math.random() * 10) + 1;
      setPercent((currentPercentage) => {
        if (currentPercentage >= 70) {
          clearInterval(timer);
          return currentPercentage;
        }

        return currentPercentage + randomNumber;
      });
    }, randomNumber * 100);
    validate();
  };

  const validate = () => {
    let cumulativeVariancesLength = 0;
    let newErrorsObj = errorsObj;

    const addError = (errorField, errorType, rowIndex, newErrorsObj) => {
      if (newErrorsObj.errors[errorField][errorType]) {
        newErrorsObj.errors[errorField][errorType].push(rowIndex);
      } else {
        newErrorsObj.count += 1;
        newErrorsObj.errors[errorField][errorType] = [rowIndex];
      }
      return newErrorsObj;
    };

    sheetData?.rowsData?.forEach(
      (
        {
          name,
          availableQuantity,
          originalPrice,
          defaultPrice,
          nameAr,
          productVariances
        },
        index
      ) => {
        const rowIndex = index + 2 + cumulativeVariancesLength;

        const productName = name || nameAr;
        if (!productName?.toString()?.trim()) {
          newErrorsObj = addError('title', 'empty', rowIndex, newErrorsObj);
        }
        if (availableQuantity?.length && isNaN(availableQuantity)) {
          newErrorsObj = addError(
            'availableQuantity',
            'notNumber',
            rowIndex,
            newErrorsObj
          );
        }
        if (
          (defaultPrice?.length && isNaN(defaultPrice)) ||
          (originalPrice?.length && isNaN(originalPrice))
        ) {
          newErrorsObj = addError('price', 'notNumber', rowIndex, newErrorsObj);
        } else if (
          (defaultPrice && Number(defaultPrice) < COD_MIN_MAX.MIN) ||
          (originalPrice && Number(originalPrice) < COD_MIN_MAX.MIN)
        ) {
          newErrorsObj = addError(
            'price',
            'lessThanMin',
            rowIndex,
            newErrorsObj
          );
        } else if (
          originalPrice &&
          Number(originalPrice) < Number(defaultPrice)
        ) {
          newErrorsObj = addError(
            'price',
            'greaterThanOriginal',
            rowIndex,
            newErrorsObj
          );
        } else if (!!originalPrice && !defaultPrice) {
          newErrorsObj = addError(
            'price',
            'invalidSaleValue',
            rowIndex,
            newErrorsObj
          );
        } else if (!!productVariances?.length) {
          const variantsErrorIndices = findLowerOriginalPriceIndices(
            productVariances,
            rowIndex
          );

          if (!!variantsErrorIndices?.length) {
            variantsErrorIndices.map(
              (index) =>
                (newErrorsObj = addError(
                  'price',
                  'greaterThanOriginal',
                  rowIndex + 1 + index,
                  newErrorsObj
                ))
            );
          }
          productVariances.forEach(
            ({ variantPrice, variantOriginalPrice, options }, index) => {
              const hasUndefinedOptionName = options?.some(
                (option) => option.optionName === undefined
              );

              if (
                (variantPrice && Number(variantPrice) < COD_MIN_MAX.MIN) ||
                (originalPrice &&
                  Number(variantOriginalPrice) < COD_MIN_MAX.MIN)
              ) {
                newErrorsObj = addError(
                  'price',
                  'lessThanMin',
                  rowIndex + 1 + index,
                  newErrorsObj
                );
              } else if (hasUndefinedOptionName) {
                newErrorsObj = addError(
                  'optionName',
                  'empty',
                  rowIndex + 1 + index,
                  newErrorsObj
                );
              }
            }
          );
        }
        cumulativeVariancesLength += productVariances?.length || 0;
      }
    );

    setErrorsObj(newErrorsObj);
    if (newErrorsObj.count) {
      sendSegment('upload_product_errors', {
        'Product Errors': newErrorsObj.count
      });
      setIsUploadBtnDisabled(true);
    } else {
      addMultipleProducts(finishValidatingAndUploading);
    }
  };

  useEffect(() => {
    sendSegment('product_reupload');
    startValidatingAndUploading();

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (percent >= 70 && percent < 100 && errorsObj.count) {
      finishValidatingAndUploading();
    }
  }, [percent]);

  return (
    <div className="br-uploading-file">
      <img src={BulkIcon} alt="upload-icon" />
      <div className="br-uploading-file__progress">
        <span className="br-uploading-file__progress-text">{percent}%</span>
        <Progress percent={percent} showInfo={false} />
      </div>

      <div>
        <span className="br-uploading-file__title">
          {intl.formatMessage({
            id: 'products.import_multiple_products.uploading.title'
          })}
        </span>
        <span className="br-uploading-file__file-name">
          {sheetData?.name || 'N/A'}
        </span>
      </div>
      <div className="br-uploading-file__subtitle">
        {intl.formatMessage({
          id: 'products.import_multiple_products.uploading.subtitle'
        })}
      </div>
    </div>
  );
};

const PreviewErrors = ({
  intl,
  sheetData,
  handleReplace,
  errorsObj,
  setModalWidth,
  isSmallMobileScreen,
  isLargeMobileScreen
}) => {
  const [dataSource, setDataSource] = useState([]);

  const columns = [
    {
      key: 'title',
      title: intl.formatMessage({
        id: 'products.import_multiple_products.uploading.table.header.column_name'
      }),
      dataIndex: 'columnName',
      width: 138
    },
    {
      key: 'issue',
      title: intl.formatMessage({
        id: 'products.import_multiple_products.uploading.table.header.issue'
      }),
      dataIndex: 'issue',
      width: 300
    },
    {
      key: 'inRows',
      title: intl.formatMessage({
        id: 'products.import_multiple_products.uploading.table.header.in_rows'
      }),
      dataIndex: 'inRows',
      width: 272,
      render: (rows) => {
        const maxCharCount = 28;
        let joinedRows = rows?.join(',');
        let restRowsCount;
        let restJoinedRows;
        const exceededMax = joinedRows.length > maxCharCount;
        if (exceededMax) {
          joinedRows = joinedRows.substring(
            0,
            joinedRows[maxCharCount] === ',' ? maxCharCount + 1 : maxCharCount
          );
          joinedRows = joinedRows.substring(0, joinedRows.lastIndexOf(','));
          const shownRows = joinedRows
            .split(',')
            .filter((num) => !isNaN(parseInt(num)));
          restRowsCount = rows.length - shownRows.length;
          restJoinedRows = rows.slice(shownRows.length).join(', ');
          joinedRows += '...';
        }
        return (
          <span className="br-in-rows-column">
            {joinedRows}
            {exceededMax && (
              <Tooltip
                title={restJoinedRows}
                placement={isLargeMobileScreen ? 'topRight' : 'top'}
                getPopupContainer={(trigger) =>
                  trigger.parentElement.parentElement.parentElement
                }
                overlayClassName="br-in-rows-column__tooltip"
              >
                <span className="br-in-rows-column__tag caption">
                  +{restRowsCount}
                </span>
              </Tooltip>
            )}
          </span>
        );
      }
    }
  ];

  const getDataSource = () => {
    const errorsTypes = {
      title: {
        empty: {
          name: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.name.column_name'
          }),
          msg: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.name.issue_required'
          })
        }
      },
      availableQuantity: {
        notNumber: {
          name: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.available_quantity.column_name'
          }),
          msg: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.available_quantity.issue_numbers_only'
          })
        }
      },
      price: {
        empty: {
          name: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.column_name'
          }),
          msg: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.issue_numbers_only'
          })
        },
        notNumber: {
          name: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.column_name'
          }),
          msg: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.issue_numbers_only'
          })
        },
        lessThanMin: {
          name: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.column_name'
          }),
          msg: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.issue_less_min'
          })
        },
        greaterThanMax: {
          name: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.column_name'
          }),
          msg: intl.formatMessage(
            {
              id: 'products.import_multiple_products.uploading.table.rows.price.issue_greater_max'
            },
            {
              value: intl.formatMessage(
                {
                  id: COUNTRY_CURRENCY_MODIFIED
                },
                {
                  cod: COD_MIN_MAX.MAX
                }
              )
            }
          )
        },
        greaterThanOriginal: {
          name: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.column_name'
          }),
          msg: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.issue_greater_original'
          })
        },
        invalidSaleValue: {
          name: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.price.column_name'
          }),
          msg: intl.formatMessage({
            id: 'products.add_products.price.price_field.error_msgs.empty'
          })
        }
      },
      optionName: {
        empty: {
          name: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.option_name.column_name'
          }),
          msg: intl.formatMessage({
            id: 'products.import_multiple_products.uploading.table.rows.option_name.issue_required'
          })
        }
      }
    };

    if (errorsObj.count) {
      setModalWidth(768);
    }

    // NOTE: Need to be improved and automated
    setDataSource([
      ...(errorsObj.errors.title?.empty
        ? [
            {
              key: 'title',
              columnName: errorsTypes.title.empty.name,
              issue: errorsTypes.title.empty.msg,
              inRows: errorsObj.errors.title.empty
            }
          ]
        : []),
      ...(errorsObj.errors.availableQuantity?.notNumber
        ? [
            {
              key: 'availableQuantity',
              columnName: errorsTypes.availableQuantity.notNumber.name,
              issue: errorsTypes.availableQuantity.notNumber.msg,
              inRows: errorsObj.errors.availableQuantity.notNumber
            }
          ]
        : []),
      ...(errorsObj.errors.price?.empty
        ? [
            {
              key: 'priceEmpty',
              columnName: errorsTypes.price.empty.name,
              issue: errorsTypes.price.empty.msg,
              inRows: errorsObj.errors.price.empty
            }
          ]
        : []),
      ...(errorsObj.errors.price?.notNumber
        ? [
            {
              key: 'priceNotNumber',
              columnName: errorsTypes.price.notNumber.name,
              issue: errorsTypes.price.notNumber.msg,
              inRows: errorsObj.errors.price.notNumber
            }
          ]
        : []),
      ...(errorsObj.errors.price?.greaterThanOriginal
        ? [
            {
              key: 'greaterThanOriginal',
              columnName: errorsTypes.price.greaterThanOriginal.name,
              issue: errorsTypes.price.greaterThanOriginal.msg,
              inRows: errorsObj.errors.price.greaterThanOriginal
            }
          ]
        : []),
      ...(errorsObj.errors.price?.invalidSaleValue
        ? [
            {
              key: 'invalidSaleValue',
              columnName: errorsTypes.price.invalidSaleValue.name,
              issue: errorsTypes.price.invalidSaleValue.msg,
              inRows: errorsObj.errors.price.invalidSaleValue
            }
          ]
        : []),
      ...(errorsObj.errors.price?.lessThanMin
        ? [
            {
              key: 'priceLessThanMin',
              columnName: errorsTypes.price.lessThanMin.name,
              issue: errorsTypes.price.lessThanMin.msg,
              inRows: errorsObj.errors.price.lessThanMin
            }
          ]
        : []),
      ...(errorsObj.errors.optionName?.empty
        ? [
            {
              key: 'optionEmpty',
              columnName: errorsTypes.optionName.empty.name,
              issue: errorsTypes.optionName.empty.msg,
              inRows: errorsObj.errors.optionName.empty
            }
          ]
        : [])
    ]);
  };

  useEffect(() => {
    getDataSource();
  }, []);

  return (
    <div className="br-uploaded-file">
      <PreviewFile
        intl={intl}
        sheetData={sheetData}
        handleReplace={handleReplace}
        errorsCount={errorsObj.count}
      />
      {!!errorsObj.count && (
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{
            y: isSmallMobileScreen ? 150 : 215
          }}
        />
      )}
    </div>
  );
};

const ImportMultipleProductsModal = ({
  intl,
  close,
  openAddProductModal,
  mobileScreenSizes: { isSmallMobileScreen, isLargeMobileScreen },
  onSuccess = () => {},
  hideBackButton = false,
  ...props
}) => {
  const [sheetData, setSheetData] = useState(null);
  const [isUploadBtnDisabled, setIsUploadBtnDisabled] = useState(true);
  const [currentStepKey, setCurrentStepKey] = useState(UPLOAD_FILE);
  const [modalWidth, setModalWidth] = useState(536);
  const [errorsObj, setErrorsObj] = useState({
    count: 0,
    errors: {
      title: {},
      availableQuantity: {},
      price: {},
      optionName: {}
    }
  });
  const [isUploading, setIsUploading] = useState(false);

  const { XLSX, CSV } = UPLOADED_FILE_TYPES;

  const reupload = (replaceFile) => {
    if (!replaceFile) {
      setSheetData(null);
    }

    setModalWidth(536);
    setIsUploadBtnDisabled(false);
    setErrorsObj({
      count: 0,
      errors: {
        title: {},
        availableQuantity: {},
        price: {}
      }
    });
  };

  const handleReplace = (props) => {
    sendSegment('PRODUCTS_ADD-MULTIPLE_5.REUPLOAD');
    reupload(true);
    beforeUpload(props);
  };

  const addMultipleProducts = async (callback) => {
    const products = sheetData?.rowsData?.map((product) => ({
      ...product,
      description: product?.description?.toString(),
      showDiscounts: true,
      quantity:
        product?.availableQuantity !== undefined &&
        product?.availableQuantity !== ''
          ? Math.floor(product?.availableQuantity)
          : 0,
      defaultPrice: !product?.defaultPrice
        ? product?.defaultPrice
        : parseFloat(Number(product?.defaultPrice).toFixed(2)),
      originalPrice: !product?.originalPrice
        ? product?.originalPrice
        : parseFloat(Number(product?.originalPrice).toFixed(2)),
      ...(product?.category && {
        category: {
          categoryName: product?.category?.toString(),
          subCategoryName:
            product?.category && product?.subCategory
              ? product?.subCategory?.toString()
              : undefined
        }
      }),
      isActive:
        product?.disabled === undefined
          ? false
          : stringToBoolean(product?.disabled),
      referenceId: !isNaN(product?.refId)
        ? product?.refId?.toString()
        : product?.refId,
      images: [product?.imageURL],
      defaultImage:
        product?.imageURL ||
        (!!product?.productVariances?.length &&
          product?.productVariances[0]?.variantDefaultImage) ||
        null
    }));

    const payload = {
      products
    };

    try {
      await createMultipleProducts(payload);
      const count = products?.length;
      products?.forEach(({ disabled, category, availableQuantity }) => {
        sendSegment('PRODUCTS_ADDED-MULTIPLE', {
          'IMPORT-PRODUCT-COUNT': count,
          ACTIVE: !!disabled,
          CATEGORIES: !!category,
          QUANTITY: availableQuantity === 1 ? 'Default' : 'Changed'
        });
      });
      await callback();
      close();
      sendSegment('product_uploaded_success');
      notify({
        msg: intl.formatMessage(
          {
            id: 'products.import_multiple_products.uploading.created_products_successfully'
          },
          {
            count
          }
        ),
        type: 'success'
      });
      onSuccess(count);
    } catch (e) {
      setIsUploadBtnDisabled(true);
      setCurrentStepKey(UPLOAD_FILE);
      notify({ msg: e?.message });
      reupload();
    }
  };

  const afterUpload = (data) => {
    setSheetData(data);
    setIsUploading(false);
    setCurrentStepKey(PREVIEW_FILE);
  };

  const handleDownloadSampleTemplate = () => {
    sendSegment('PRODUCTS_ADD-MULTIPLE_1.SAMPLE');
    return saveAs(
      ProductsSampleXLSX,
      'Products Sample XLSX - Add Products Here.xlsx'
    );
  };

  const renderSampleTemplate = (children, className) => (
    <a
      className={classnames('br-sample-template', className)}
      onClick={handleDownloadSampleTemplate}
    >
      {children}
    </a>
  );

  const renderYoutubeVideo = () => {
    return (
      <div className="br-how-to-upload__container">
        <img alt="" src={YoutubeReference} />
        <div className="br-how-to-upload__content">
          <span className="caption-lg">0:30</span>
          <span className="heading">
            {intl.formatMessage({
              id: 'products.import_multiple_products.how_to_upload'
            })}
          </span>
        </div>
      </div>
    );
  };

  const getFileErrorMsg = (errorType) => {
    const { INVALID_FILE, EMPTY_FILE, EXCEEDED_MAX_ROWS } =
      FILE_SHEET_UPLOAD_ERROR_TYPES;

    switch (errorType) {
      case INVALID_FILE:
        return intl.formatMessage(
          {
            id: 'products.import_multiple_products.upload.error_msgs.invalid_file'
          },
          {
            span: (children) => renderSampleTemplate(children, 'error')
          }
        );

      case EMPTY_FILE:
        return intl.formatMessage(
          {
            id: 'products.import_multiple_products.upload.error_msgs.empty_file'
          },
          {
            span: (children) => renderSampleTemplate(children, 'error')
          }
        );

      case EXCEEDED_MAX_ROWS:
        return intl.formatMessage(
          {
            id: 'products.import_multiple_products.upload.error_msgs.max_count'
          },
          {
            maxNumOfProducts: SHEET_MAX_NUM_OF_PRODUCTS
          }
        );

      default:
        return;
    }
  };

  const beforeUpload = (file) => {
    sendSegment('PRODUCTS_ADD-MULTIPLE_2.UPLOAD');
    setIsUploading(true);

    const { name, type, size } = file;
    const isRequiredType = [XLSX, CSV].includes(type);

    if (isRequiredType) {
      let rows = [];
      let fileError;

      convertSheetToJSON(file)
        .then((rowsData) => {
          rows = rowsData;
        })
        .catch(({ errorType, rowsData }) => {
          fileError = errorType;
          rows = rowsData || [];
        })
        .finally(() => {
          afterUpload({
            name,
            size,
            rowsData: rows,
            ...(!!fileError && {
              fileError: getFileErrorMsg(fileError)
            })
          });
        });
    } else {
      notify({
        msg: intl.formatMessage(
          {
            id: 'products.import_multiple_products.upload.error_msgs.type'
          },
          {
            maxNumOfProducts: SHEET_MAX_NUM_OF_PRODUCTS
          }
        ),
        status: -1
      });
      setIsUploading(false);
    }

    return isRequiredType;
  };

  const handleUploadFileClick = () => {
    sendSegment('upload_product', { 'Upload product images': false });
    setCurrentStepKey(VALIDATING_AND_UPLOADING);
  };

  const renderContent = () => {
    const content = {
      UPLOAD_FILE: {
        Component: UploadFile,
        props: {
          intl,
          isSmallMobileScreen,
          beforeUpload,
          isUploading,
          renderSampleTemplate,
          renderYoutubeVideo
        }
      },
      PREVIEW_FILE: {
        Component: PreviewFile,
        props: {
          intl,
          sheetData,
          setCurrentStepKey,
          handleReplace,
          renderSampleTemplate
        }
      },
      VALIDATING_AND_UPLOADING: {
        Component: ValidatingAndUploading,
        props: {
          intl,
          sheetData,
          setCurrentStepKey,
          errorsObj,
          setErrorsObj,
          addMultipleProducts,
          setIsUploadBtnDisabled
        }
      },
      PREVIEW_ERRORS: {
        Component: PreviewErrors,
        props: {
          intl,
          sheetData,
          handleReplace,
          errorsObj,
          setModalWidth,
          setCurrentStepKey,
          isSmallMobileScreen,
          isLargeMobileScreen
        }
      }
    };

    const { Component, props } = content[currentStepKey];
    return <Component {...props} />;
  };

  useEffect(() => {
    if (sheetData) {
      setIsUploadBtnDisabled(!!sheetData.fileError);
    } else {
      setIsUploadBtnDisabled(true);
      setCurrentStepKey(UPLOAD_FILE);
    }
  }, [sheetData]);

  return (
    <Modal
      {...props}
      className="br-import-multiple-products-modal"
      title={
        <div className="br-import-multiple-products-modal__header">
          <div className="br-import-multiple-products-modal-subtitles__wrap">
            <div className="display-xs">
              {intl.formatMessage({
                id: 'products.import_multiple_products.title'
              })}
            </div>
            <div className="br-import-multiple-products-modal__subtitle body">
              {intl.formatMessage({
                id: 'products.import_multiple_products.subtitle'
              })}
            </div>
          </div>
          <CloseIcon onClick={close} />
        </div>
      }
      footer={
        <div className="br-import-multiple-products-modal__footer">
          {currentStepKey === PREVIEW_FILE ? (
            <Link
              className="br-import-multiple-products-modal__help-link"
              to={{ pathname: IMPORT_MULTIPLE_PRODUCTS_HELP_LINK }}
              target="_blank"
            >
              {intl.formatMessage({
                id: `products.import_multiple_products.upload.footer_help_link${
                  isSmallMobileScreen ? '_short' : ''
                }`
              })}
              <ExternalLinkIcon
                className={classnames({
                  mirror: intl.locale === LOCALE.AR
                })}
              />
            </Link>
          ) : (
            <span />
          )}
          <div className="br-import-multiple-products-modal__footer__actions">
            <BRButton
              label={intl.formatMessage({
                id: 'common.cancel'
              })}
              onClick={close}
              disabled={currentStepKey === VALIDATING_AND_UPLOADING}
            />
            <BRButton
              type="primary"
              label={intl.formatMessage({
                id: 'products.import_multiple_products.confirm'
              })}
              disabled={isUploadBtnDisabled}
              loading={currentStepKey === VALIDATING_AND_UPLOADING}
              onClick={handleUploadFileClick}
            />
          </div>
        </div>
      }
      width={modalWidth}
      centered
    >
      {renderContent()}
    </Modal>
  );
};

export default injectIntl(mediaHook(ImportMultipleProductsModal));
